import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { writeAuditLog } from '@/services/audit'
import type { OperationalObjective } from '@/types'

export const useObjectivesStore = defineStore('objectives', () => {
  const objectives = ref<OperationalObjective[]>([])
  const loading = ref(false)

  let unsubFn: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToObjectives(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubFn) return
    if (unsubFn) unsubFn()
    _incidentId = incidentId
    _opId = opId
    loading.value = true
    const q = query(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'objectives'),
      orderBy('order'),
    )
    unsubFn = onSnapshot(q, (snap) => {
      objectives.value = snap.docs.map((d) => ({ objective_id: d.id, ...d.data() }) as OperationalObjective)
      loading.value = false
    })
  }

  async function addObjective(incidentId: string, opId: string, text: string) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      const docRef = await addDoc(
        collection(db, 'incidents', incidentId, 'operational_periods', opId, 'objectives'),
        { text, order: objectives.value.length, created_at: serverTimestamp() },
      )
      await writeAuditLog(incidentId, 'iap.objective_added', 'objective', docRef.id, null, { text })
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function updateObjective(incidentId: string, opId: string, objectiveId: string, text: string) {
    const syncStore = useSyncStore()
    syncStore.pendingWrites++
    try {
      await updateDoc(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'objectives', objectiveId),
        { text },
      )
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function removeObjective(incidentId: string, opId: string, objectiveId: string) {
    const before = objectives.value.find((o) => o.objective_id === objectiveId) ?? null
    await deleteDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'objectives', objectiveId),
    )
    await writeAuditLog(incidentId, 'iap.objective_removed', 'objective', objectiveId, before as Record<string, unknown> | null, null)
  }

  async function reorderObjectives(incidentId: string, opId: string, ordered: OperationalObjective[]) {
    const batch = writeBatch(db)
    ordered.forEach((obj, index) => {
      batch.update(
        doc(db, 'incidents', incidentId, 'operational_periods', opId, 'objectives', obj.objective_id),
        { order: index },
      )
    })
    await batch.commit()
  }

  function teardown() {
    if (unsubFn) { unsubFn(); unsubFn = null }
    _incidentId = ''
    _opId = ''
    objectives.value = []
    loading.value = false
  }

  return { objectives, loading, subscribeToObjectives, addObjective, updateObjective, removeObjective, reorderObjectives, teardown }
})
